import {useState} from "react";
import {Box, Collapse, Stack, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {WordGram} from "../../../../../sdo/WordGram.ts";

interface ContentWordGramsProps {
    leftWords: WordGram[];
    rightWords: WordGram[];
    inFrameWords: WordGram[];
}

interface GramSectionProps {
    title: string;
    grams: WordGram[];
    expanded: boolean;
    onToggle: () => void;
}

function GramPill({gram}: { gram: WordGram }) {
    return (
        <Box sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "4px 10px",
            borderRadius: "10px",
            border: "0.5px solid rgba(112, 147, 160, 0.35)",
            backgroundColor: "rgba(26, 37, 48, 0.85)",
        }}>
            <Typography sx={{color: "#f2f5fa", fontSize: "0.72rem", fontWeight: 500}}>
                {gram.word}
            </Typography>
            <Typography sx={{color: "#38ff82", fontSize: "0.70rem", fontWeight: 700}}>
                {gram.count}
            </Typography>
        </Box>
    );
}

function GramSection({title, grams, expanded, onToggle}: GramSectionProps) {
    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={onToggle}
                sx={{
                    borderBottom: "1px solid #3F464F",
                    pb: 0.5,
                    mb: 1,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": {opacity: 0.85},
                }}
            >
                {expanded
                    ? <ExpandMoreIcon sx={{fontSize: 20, color: "#a8b8c0"}}/>
                    : <ChevronRightIcon sx={{fontSize: 20, color: "#a8b8c0"}}/>}
                <Typography sx={{
                    color: "#a8b8c0",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    letterSpacing: 0.3,
                }}>
                    {title}
                </Typography>
                <Typography sx={{color: "#38ff82", fontSize: "0.75rem", fontWeight: 700}}>
                    ({grams.length})
                </Typography>
            </Stack>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {grams.length > 0 ? (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: "6px", padding: "4px 0 8px 0"}}>
                        {grams.map((gram) => (
                            <GramPill key={gram.word} gram={gram}/>
                        ))}
                    </Box>
                ) : (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.75rem",
                        fontStyle: "italic",
                        padding: "8px 0",
                    }}>
                        No entries
                    </Typography>
                )}
            </Collapse>
        </Box>
    );
}

function ContentWordGrams({leftWords, rightWords, inFrameWords}: ContentWordGramsProps) {
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set([0, 1, 2]));

    const toggle = (idx: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    return (
        <Stack direction="column" spacing={2} sx={{height: "100%", minHeight: 0, overflowY: "auto"}}>
            <GramSection title="Left Words" grams={leftWords} expanded={expanded.has(0)} onToggle={() => toggle(0)}/>
            <GramSection title="Right Words" grams={rightWords} expanded={expanded.has(1)} onToggle={() => toggle(1)}/>
            <GramSection title="In Frame Words" grams={inFrameWords} expanded={expanded.has(2)} onToggle={() => toggle(2)}/>
        </Stack>
    );
}

export default ContentWordGrams;